import { Coding } from "@emed-labs/resource-api-models";
import { Env, Patient, Person } from "@emed-labs/virtual-lab-models";
import { Config } from "../environment/config";
import { BrowserUtils } from "../utils/BrowserUtils";
import { UserUtils } from "../utils/UserUtils";

export interface CommonNavigatorAttributes {
	ACCOUNT_FIRST_NAME: string;
	ACCOUNT_LAST_NAME: string;
	ACCOUNT_DOB: string;
	ACCOUNT_DOB_DISPLAY: string;
	ACCOUNT_EMAIL: string;
	USER_FIRST_NAME: string;
	USER_LAST_NAME: string;
	USER_ENCODED_NAME: string;
	USER_DOB: string;
	USER_DOB_DISPLAY: string;
	USER_EMAIL: string;
	USER_ENCODED_EMAIL: string;
	USER_PHONE: string;
	SESSION_ID: string;
	PROCEDURE_ID: string;
	CONTACT_ID: string;
	CDN_BASE_URL: string;
	CORE_BASE_URL: string;
	TIMESTAMP: string;
	PROCTOR_METADATA?: string;
}

export class NavigatorDataUtils {
	static createNavigatorAttributes<T = {}>({
		patient,
		sessionID,
		contactID,
		extraData,
		procedureID,
		actor,
		env,
		sessionTags
	}: {
		sessionID: string;
		procedureID: string;
		contactID: string;
		patient: Patient;
		actor: Patient | Person;
		extraData?: T;
		env: Env.Reader;
		sessionTags?: Coding[];
	}): CommonNavigatorAttributes & T {
		const userDateOfBirth = patient?.birthDate;
		const accountDateOfBirth = actor?.birthDate || patient?.birthDate;

		const patientName = patient?.name?.[0];
		const userEncodedName = encodeURIComponent(`${patientName?.given} ${patientName?.family}`);
		const userEncodedFirstName = encodeURIComponent(`${patientName?.given[0]}`);
		const userEncodedLastName = encodeURIComponent(`${patientName?.family}`);

		const actorName = actor?.name?.[0];

		const actorPhone = actor?.telecom?.find((t) => t.system === "phone");
		const actorEmail = actor?.telecom?.find((t) => t.system === "email");

		const patientPhone = patient?.telecom?.find((t) => t.system === "phone");
		const patientEmail = patient?.telecom?.find((t) => t.system === "email");

		const userEncodedPhoneNumber = encodeURIComponent(patientPhone?.value);
		const encodedEmail = encodeURIComponent(patientEmail?.value);

		const timeStamp = new Date().toLocaleDateString("en-us", {
			weekday: "short",
			year: "numeric",
			month: "short",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
			timeZoneName: "short"
		});

		const accountDOBDisplay =
			accountDateOfBirth?.length > 0
				? UserUtils.formatISODateWithOrdinal(UserUtils.normalizeDOB(accountDateOfBirth))
				: "";
		const userDOBDisplay =
			userDateOfBirth?.length > 0 ? UserUtils.formatISODateWithOrdinal(UserUtils.normalizeDOB(userDateOfBirth)) : "";

		// Extract PROCTOR_METADATA tag from session tags
		const proctorMetadataTag = sessionTags?.find((tag) => tag.system === "https://emed.com/vlab/proctor-metadata");
		const proctorMetadata = proctorMetadataTag?.display || undefined;

		return {
			...extraData,
			CDN_BASE_URL: env.get(Config.SCRIPTS_BASE_URL),
			SESSION_ID: sessionID,
			ACCOUNT_FIRST_NAME: actorName?.given.join(" "),
			ACCOUNT_LAST_NAME: actorName?.family,
			ACCOUNT_DOB: accountDateOfBirth,
			ACCOUNT_DOB_DISPLAY: accountDOBDisplay,
			ACCOUNT_EMAIL: actorEmail?.value,
			USER_FIRST_NAME: patientName?.given.join(" "),
			USER_LAST_NAME: patientName?.family,
			USER_PHONE: patientPhone?.value,
			USER_ENCODED_FIRST_NAME: userEncodedFirstName,
			USER_ENCODED_LAST_NAME: userEncodedLastName,
			USER_ENCODED_PHONE: userEncodedPhoneNumber,
			USER_ENCODED_NAME: userEncodedName,
			USER_DOB: userDateOfBirth,
			USER_DOB_DISPLAY: userDOBDisplay,
			USER_EMAIL: patientEmail?.value,
			USER_ENCODED_EMAIL: encodedEmail,
			USER_STATE: patient?.address?.[0]?.state,
			PROCEDURE_ID: procedureID,
			CONTACT_ID: contactID,
			//scripst may have CORE_BASE_URL, but needs to be the vlab ui base url
			CORE_BASE_URL: BrowserUtils.getOrigin(),
			TIMESTAMP: timeStamp,
			PROCTOR_METADATA: proctorMetadata
		};
	}
}
